import { ActivatedRouteSnapshot, Route } from '@angular/router';

import assert from 'tiny-invariant';

import { IonicRouteStrategy } from '@ionic/angular';

import { IONIC_PAGE_SHELL_COMPONENT_CLASS_KEY } from './routes-wrapper.util';

/**
 * Route `data` key naming the page a route shows: routes carrying the same value
 * are one page, kept across their navigations, whose params change in place.
 */
export const ROUTE_REUSE_KEY = 'routeReuseKey';

// Read the route's own config: snapshot `data` inherits a parent's onto
// empty-path children, which must not make them reusable.
function getRouteReuseKey(routeConfig: Route | null): unknown {
  return routeConfig?.data?.[ROUTE_REUSE_KEY];
}

/** Whether the route config carries a `ROUTE_REUSE_KEY`. */
export function isRouteReuseKeyed(routeConfig: Route | null): boolean {
  return getRouteReuseKey(routeConfig) !== undefined;
}

/** Whether both route configs carry the same defined `ROUTE_REUSE_KEY`. */
export function isRouteReuseKeyShared(
  routeConfig: Route | null,
  otherRouteConfig: Route | null,
): boolean {
  const key = getRouteReuseKey(routeConfig);
  return key !== undefined && key === getRouteReuseKey(otherRouteConfig);
}

// Under wrapRoutesInIonicPageShell every leaf's component is the shell, which
// renders the page named in its data.
function getRoutePageComponent(route: ActivatedRouteSnapshot): unknown {
  return route.data[IONIC_PAGE_SHELL_COMPONENT_CLASS_KEY] ?? route.component;
}

// IonicRouteStrategy rebuilds the page on any param change; keyed routes opt
// out of that (#11677 A28).
export class RouteReuseKeyedStrategy extends IonicRouteStrategy {
  override shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    current: ActivatedRouteSnapshot,
  ): boolean {
    if (isRouteReuseKeyShared(future.routeConfig, current.routeConfig)) {
      // A reused page keeps rendering the current route's component.
      assert(
        getRoutePageComponent(future) === getRoutePageComponent(current),
        `Routes sharing a ${ROUTE_REUSE_KEY} render different components`,
      );
      return true;
    }
    return super.shouldReuseRoute(future, current);
  }
}
